/**
 * Task / Project Management — feature configuration.
 *
 * Centralizes the Kanban columns (statuses), priority levels, and their
 * display colors/order in ONE place — same convention as
 * `src/utils/reimbursement-config.js` — so the board layout can be
 * reconfigured (renamed/reordered/added columns) without touching component
 * markup.
 */

// ------------------------------------------------------------------------
// Statuses / Kanban columns — order here IS the left-to-right column order.
// ------------------------------------------------------------------------
export const TASK_STATUSES = {
  TODO: 'To Do',
  IN_PROGRESS: 'In Progress',
  REVIEW: 'Review',
  DONE: 'Done',
}

export const TASK_STATUS_COLUMNS = [
  { key: TASK_STATUSES.TODO, label: 'To Do', variant: 'secondary', icon: 'CircleIcon' },
  { key: TASK_STATUSES.IN_PROGRESS, label: 'In Progress', variant: 'info', icon: 'ClockIcon' },
  { key: TASK_STATUSES.REVIEW, label: 'Review', variant: 'warning', icon: 'EyeIcon' },
  { key: TASK_STATUSES.DONE, label: 'Done', variant: 'success', icon: 'CheckCircleIcon' },
]

export const DEFAULT_TASK_STATUS = TASK_STATUSES.TODO

/**
 * Synonyms some APIs / older rows may store instead of the canonical
 * Kanban labels above. Mapped BEFORE the unknown→`To Do` fallback so a
 * "Completed" task is never treated as open (and therefore never escalated).
 */
const TASK_STATUS_ALIASES = {
  done: TASK_STATUSES.DONE,
  completed: TASK_STATUSES.DONE,
  complete: TASK_STATUSES.DONE,
  closed: TASK_STATUSES.DONE,
  finished: TASK_STATUSES.DONE,
}

export function normalizeTaskStatus(task) {
  const raw = task && (task.status ?? task.taskStatus ?? task.task_status)
  const key = String(raw || '').toLowerCase().trim()
  const match = TASK_STATUS_COLUMNS.find(col => col.key.toLowerCase() === key)
  if (match) return match.key
  if (TASK_STATUS_ALIASES[key]) return TASK_STATUS_ALIASES[key]
  return DEFAULT_TASK_STATUS
}

/**
 * True when a task is finished and must not receive escalation/reminder
 * mail, push, or bell inbox items. Uses normalized Kanban status first, then
 * falls back to a persisted `completedAt` timestamp when the status field
 * is stale (e.g. list row not yet refreshed after PATCH .../status).
 */
export function isTaskComplete(task) {
  if (!task || task.isDeleted) return false
  if (normalizeTaskStatus(task) === TASK_STATUSES.DONE) return true
  const completedAt = task.completedAt ?? task.completed_at
  if (!completedAt) return false
  const completed = new Date(completedAt)
  return !Number.isNaN(completed.getTime())
}

export function statusVariant(statusOrTask) {
  const status = typeof statusOrTask === 'string' ? statusOrTask : normalizeTaskStatus(statusOrTask)
  const match = TASK_STATUS_COLUMNS.find(col => col.key === status)
  return match ? match.variant : 'secondary'
}

// ------------------------------------------------------------------------
// Priorities — order here is highest-first, used to rank cards when no
// explicit drag order is stored yet.
// ------------------------------------------------------------------------
export const TASK_PRIORITIES = {
  URGENT: 'Urgent',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

export const TASK_PRIORITY_LEVELS = [
  { key: TASK_PRIORITIES.URGENT, label: 'Urgent', variant: 'danger', rank: 0 },
  { key: TASK_PRIORITIES.HIGH, label: 'High', variant: 'warning', rank: 1 },
  { key: TASK_PRIORITIES.MEDIUM, label: 'Medium', variant: 'primary', rank: 2 },
  { key: TASK_PRIORITIES.LOW, label: 'Low', variant: 'secondary', rank: 3 },
]

export const DEFAULT_TASK_PRIORITY = TASK_PRIORITIES.MEDIUM

export function normalizeTaskPriority(task) {
  const raw = task && (task.priority || task.taskPriority)
  const match = TASK_PRIORITY_LEVELS.find(p => p.key.toLowerCase() === String(raw || '').toLowerCase())
  return match ? match.key : DEFAULT_TASK_PRIORITY
}

export function priorityVariant(priorityOrTask) {
  const priority = typeof priorityOrTask === 'string' ? priorityOrTask : normalizeTaskPriority(priorityOrTask)
  const match = TASK_PRIORITY_LEVELS.find(p => p.key === priority)
  return match ? match.variant : 'secondary'
}

export function priorityRank(priorityOrTask) {
  const priority = typeof priorityOrTask === 'string' ? priorityOrTask : normalizeTaskPriority(priorityOrTask)
  const match = TASK_PRIORITY_LEVELS.find(p => p.key === priority)
  return match ? match.rank : TASK_PRIORITY_LEVELS.length
}

// ------------------------------------------------------------------------
// Due-date escalation — computed PURELY from (now, the task's CURRENT
// `dueDate`, its CURRENT `status`) every single time it's read; nothing
// about the result is ever cached/persisted anywhere, so editing a task's
// due date takes effect immediately and a task can never be stuck showing
// a stale escalation level. Three tiers, increasing urgency the longer a
// task sits overdue — conceptually the same "day-based follow-up ladder"
// idea as the invoice module's existing overdue staging
// (`InvoiceDashboard.vue#getFollowUpStatus`: Friendly Reminder -> Payment
// Reminder -> Escalation -> Finance Escalation, at 3/7/15 days), but tuned
// tighter here: tasks are day-to-day work items, not invoices that can
// legitimately take weeks to collect, so a task ignored for a week is
// already a real problem. No task-specific escalation schedule/config
// exists anywhere else in the codebase to defer to instead — these are new,
// deliberately conservative defaults.
// ------------------------------------------------------------------------
// (2026-07-23) Switched from solid `warning`/`danger`/`dark` variants to
// their `light-*` counterparts — the solid fills read as too heavy/dark
// next to the rest of the card's calm `light-*` Priority/Status badges.
// Urgency is still clearly conveyed via label + (for `final`) a bold,
// dark-red text override in `TaskCard.vue` — just without a jarring solid
// fill on every card, matching the same "sober" badge treatment already
// used everywhere else on this card.
// Escalation 1 fires as soon as the deadline has passed — including the
// same calendar day after `dueTime` / 6 PM default (`daysOverdue === 0`).
// Tiers 2/final still key off full calendar days past the due date.
export const TASK_ESCALATION_LEVELS = [
  { key: 'escalation_1', label: 'Escalation 1', variant: 'light-warning', minDaysOverdue: 0 },
  { key: 'escalation_2', label: 'Escalation 2', variant: 'light-danger', minDaysOverdue: 3 },
  { key: 'final', label: 'FINAL', variant: 'light-danger', minDaysOverdue: 7 },
]

/**
 * The MOMENT (date + time, not just a calendar day) a task's deadline
 * actually falls due — `dueTime` (the `TaskFormModal.vue` "Due Time"
 * field, `'H:i'`) combined with `due`'s calendar date when set, otherwise
 * a default of 6:00 PM (18:00) on that same date. Only ever matters for a
 * task due TODAY — a task due on an earlier day has already fully elapsed
 * regardless of what its nominal deadline hour was.
 *
 * Per product direction: a same-day task with no explicit due time
 * shouldn't be treated as overdue the instant the calendar date rolls
 * over (midnight IST) — "due today" realistically means "by end of
 * business today," not "by the stroke of midnight" — so 6 PM IST is used
 * as a reasonable default end-of-day deadline instead.
 *
 * Timezone note: deliberately built with the LOCAL `Date` constructor
 * (`new Date(year, month, date, hour, minute)`), the same local-component
 * approach already used for `dueDay`/`nowDay` right below and throughout
 * this module — this app's users are all IST, so the browser's local
 * clock already IS IST for them; no explicit UTC/offset math or new
 * date/time library is introduced (`moment`, used elsewhere in this
 * codebase, isn't imported anywhere in this dependency-free module today,
 * and pulling it in for just this one function would be inconsistent).
 */
function effectiveDeadline(due, dueTime) {
  if (typeof dueTime === 'string') {
    const match = dueTime.match(/^(\d{1,2}):(\d{2})/)
    if (match) {
      const hour = Number(match[1])
      const minute = Number(match[2])
      if (!Number.isNaN(hour) && !Number.isNaN(minute)) {
        return new Date(due.getFullYear(), due.getMonth(), due.getDate(), hour, minute, 0, 0)
      }
    }
  }
  return new Date(due.getFullYear(), due.getMonth(), due.getDate(), 18, 0, 0, 0)
}

/**
 * Parse `dueDate` to a local Date, or `null` if missing/invalid.
 * Date-only `YYYY-MM-DD` is local midnight (not UTC) so calendar-day
 * comparisons do not shift a day west of UTC.
 */
function parseDueDate(dueDate) {
  if (!dueDate) return null
  if (dueDate instanceof Date) {
    return Number.isNaN(dueDate.getTime()) ? null : dueDate
  }
  const str = String(dueDate).trim()
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnly) {
    const local = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    return Number.isNaN(local.getTime()) ? null : local
  }
  const due = new Date(str)
  if (Number.isNaN(due.getTime())) return null
  return due
}

/**
 * Midnight-to-midnight calendar-day difference in the local timezone
 * (positive = `now`'s calendar day is after `dueDate`'s). Not
 * `Math.ceil(ms/86400000)` — that would inflate same-day / partial-day
 * overdue into "1 day".
 */
function calendarDayDiff(dueDate, now) {
  const due = parseDueDate(dueDate)
  if (!due) return null
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((nowDay.getTime() - dueDay.getTime()) / 86400000)
}

/**
 * True once the task's deadline has actually passed:
 * - prior calendar day → always past (hour no longer matters)
 * - same calendar day → only after `effectiveDeadline()` (`dueTime` or 6 PM)
 * - future calendar day → not past
 */
function isPastDeadline(dueDate, now, dueTime) {
  const due = parseDueDate(dueDate)
  if (!due) return false
  const dayDiff = calendarDayDiff(due, now)
  if (dayDiff == null) return false
  if (dayDiff > 0) return true
  if (dayDiff < 0) return false
  return now.getTime() >= effectiveDeadline(due, dueTime).getTime()
}

/**
 * Full calendar days past the due date for DISPLAY / email "Days Overdue".
 * Separated from escalation triggering so same-day overdue-by-time alone
 * is NOT inflated to "1 day":
 * - deadline not yet passed → `null` (not overdue)
 * - same calendar day, past deadline → `0` ("overdue today")
 * - next calendar day → `1`, etc.
 */
function daysOverdue(dueDate, now, dueTime) {
  if (!isPastDeadline(dueDate, now, dueTime)) return null
  const dayDiff = calendarDayDiff(dueDate, now)
  return dayDiff == null ? null : Math.max(0, dayDiff)
}

/**
 * Public wrapper around `daysOverdue()` — calendar-day count for UI/email
 * (0 = overdue today by time only). `null` if not past deadline / no due date.
 */
export function getDaysOverdue(task, now = new Date()) {
  if (!task) return null
  return daysOverdue(task.dueDate || task.due_date, now, task.dueTime || task.due_time)
}

/**
 * Human-readable overdue label for tooltips / email body.
 * `0` → "overdue today (past due time)"; `N` → "N day(s) past due date".
 */
export function formatDaysOverdueLabel(days) {
  if (days == null) return ''
  if (days === 0) return 'overdue today (past due time)'
  return `${days} day(s) past due date`
}

/**
 * Pure function of (now, task) — safe to call on every render, every time,
 * with no memoization needed and no risk of returning something stale: a
 * manager pushing a task's due date out immediately makes this return a
 * lower level (or `null`) the very next time it's called, since there is
 * no separate "escalation counter" anywhere that could disagree with the
 * due date it was supposedly based on. Returns the matched tier's `key`
 * (`'escalation_1'` / `'escalation_2'` / `'final'`), or `null` when the
 * task isn't overdue, has no due date, is already Done/Completed, is
 * soft-deleted, is a recurring TEMPLATE (generator row), or is a recurring
 * OCCURRENCE (`recurringTaskId` set) — templates keep an old `dueDate`
 * forever while occurrences do the real work; occurrences get gentle
 * reminder emails only (see `TaskBoard.vue#checkTaskReminders`), never
 * this escalation ladder.
 *
 * Escalation 1 (`minDaysOverdue: 0`) fires the moment the deadline passes
 * on the due calendar day; "Days Overdue" in mail/UI still reports `0`
 * until the next calendar day.
 */
export function getEscalationLevel(task, now = new Date()) {
  if (!task) return null
  if (task.isDeleted) return null
  // Recurring series — templates and occurrences use reminders, not escalation.
  if (taskIsRecurringTemplate(task) && taskRecurringTaskId(task) == null) return null
  if (taskRecurringTaskId(task) != null) return null
  if (isTaskComplete(task)) return null
  const overdue = daysOverdue(task.dueDate || task.due_date, now, task.dueTime || task.due_time)
  if (overdue == null) return null
  let matched = null
  TASK_ESCALATION_LEVELS.forEach(level => {
    if (overdue >= level.minDaysOverdue) matched = level.key
  })
  return matched
}

/** Display metadata (label/variant) for a level key returned by `getEscalationLevel`. */
export function escalationLevelMeta(levelKey) {
  return TASK_ESCALATION_LEVELS.find(level => level.key === levelKey) || null
}

/**
 * General "is this task actually overdue" signal — true once its deadline
 * has passed (same-day after `dueTime`/6 PM, or any prior calendar day),
 * i.e. the exact same moment `getEscalationLevel()` would first return
 * `'escalation_1'`. Display "Days Overdue" may still be `0` that same day.
 */
export function isTaskOverdue(task, now = new Date()) {
  if (!task) return false
  if (task.isDeleted) return false
  // Same "never flag a template / completed task" rule as `getEscalationLevel`.
  if (taskIsRecurringTemplate(task) && taskRecurringTaskId(task) == null) return false
  if (isTaskComplete(task)) return false
  return daysOverdue(task.dueDate || task.due_date, now, task.dueTime || task.due_time) != null
}

/**
 * "Due Today" is deliberately a SEPARATE, non-urgent signal from
 * `getEscalationLevel` above — a task due today hasn't missed its
 * deadline yet, so it gets a calm informational badge instead of the
 * warning/danger escalation ladder. The instant its deadline passes
 * (its `dueTime`, or the 6 PM IST default when none is set),
 * `getEscalationLevel` takes over instead (the two are mutually exclusive
 * by construction).
 */
// (2026-07-23) `light-info` instead of solid `info` — same "too dark"
// fix as the escalation badges above; the clock icon + label already
// carry the "heads up, due today" meaning without needing a heavy fill.
export const TASK_DUE_TODAY_META = { key: 'due_today', label: 'Due Today', variant: 'light-info' }

/** Fallback card/list badge when a task is past due but has no escalation tier (e.g. recurring occurrences). */
export const TASK_OVERDUE_META = { key: 'overdue', label: 'Overdue', variant: 'light-danger' }

/** True when `task`'s due date is today AND its deadline (`dueTime`, or 6 PM IST default) hasn't passed yet AND it isn't already Done — same "never flag a completed task" rule as escalation. */
export function isDueToday(task, now = new Date()) {
  if (!task) return false
  if (task.isDeleted) return false
  if (taskIsRecurringTemplate(task) && taskRecurringTaskId(task) == null) return false
  if (isTaskComplete(task)) return false
  const dueDate = task.dueDate || task.due_date
  if (calendarDayDiff(dueDate, now) !== 0) return false
  return !isPastDeadline(dueDate, now, task.dueTime || task.due_time)
}

// ------------------------------------------------------------------------
// Recurring tasks — deliberately NOT a full RRULE/iCal model. A recurring
// task is just a normal task (the "template") with `isRecurring: true` plus
// a handful of extra fields describing how often the NEXT one should be
// generated; generated occurrences are themselves perfectly ordinary,
// non-recurring tasks (`isRecurring: false`) that only carry a
// `recurringTaskId` pointer back to the template, for traceability/badge
// display (`isPartOfRecurringSeries()` below). See `TaskBoard.vue`'s
// `checkRecurringTasks()` for the (client-side, best-effort) generation
// loop that reads these fields, and the "Recurring Tasks" section of
// `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md` for the documented backend gap.
// ------------------------------------------------------------------------
export const RECURRENCE_FREQUENCIES = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
]

export const DEFAULT_RECURRENCE_FREQUENCY = RECURRENCE_FREQUENCIES[0].key

// `value` matches native `Date#getDay()` (0 = Sunday ... 6 = Saturday) so
// `computeNextRecurrenceDate()` below can compare directly with no
// translation layer — display order is Mon-first (more natural for a work
// task list) even though the underlying values aren't sequential.
export const RECURRENCE_DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

/** Plain 1-31 options for `'monthly'`'s "On this day of the month" `<b-form-select>` — a bare number array renders fine as-is (BootstrapVue uses a primitive option's own value as both its `value` and displayed text), no `{key, label}` mapping needed. */
export const RECURRENCE_DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

/**
 * Read recurrence fields with camelCase + snake_case aliases.
 * Some `/pm/tasks` responses echo snake_case only; if the client only
 * looked at camelCase, `recurrenceLastGeneratedDate` looked unset after
 * every refresh and `checkRecurringTasks()` POSTed another occurrence.
 */
export function taskIsRecurringTemplate(task) {
  if (!task) return false
  const raw = task.isRecurring !== undefined ? task.isRecurring : task.is_recurring
  return !!raw
}

export function taskRecurringTaskId(task) {
  if (!task) return null
  const value = task.recurringTaskId ?? task.recurring_task_id
  return value === undefined || value === null || value === '' ? null : String(value)
}

export function taskRecurrenceLastGeneratedDate(task) {
  if (!task) return null
  return task.recurrenceLastGeneratedDate ?? task.recurrence_last_generated_date ?? null
}

export function taskRecurrenceFrequency(task) {
  if (!task) return null
  return task.recurrenceFrequency ?? task.recurrence_frequency ?? null
}

export function taskRecurrenceInterval(task) {
  if (!task) return null
  const value = task.recurrenceInterval ?? task.recurrence_interval
  return value === undefined || value === null ? null : value
}

export function taskRecurrenceDaysOfWeek(task) {
  if (!task) return []
  const raw = task.recurrenceDaysOfWeek ?? task.recurrence_days_of_week
  return Array.isArray(raw) ? raw : []
}

export function taskRecurrenceDayOfMonth(task) {
  if (!task) return null
  const value = task.recurrenceDayOfMonth ?? task.recurrence_day_of_month
  return value === undefined || value === null ? null : Number(value)
}

export function taskRecurrenceEndDate(task) {
  if (!task) return null
  return task.recurrenceEndDate ?? task.recurrence_end_date ?? null
}

/**
 * Collapse camelCase/snake_case recurrence aliases onto the camelCase keys
 * the rest of the client already reads. Safe to run on every list load.
 */
export function normalizeTaskRecurrenceFields(task) {
  if (!task || typeof task !== 'object') return task
  return {
    ...task,
    isRecurring: taskIsRecurringTemplate(task),
    recurrenceFrequency: taskRecurrenceFrequency(task),
    recurrenceInterval: taskRecurrenceInterval(task),
    recurrenceDaysOfWeek: taskRecurrenceDaysOfWeek(task),
    recurrenceDayOfMonth: (() => {
      const day = taskRecurrenceDayOfMonth(task)
      return Number.isFinite(day) ? day : null
    })(),
    recurrenceEndDate: taskRecurrenceEndDate(task),
    recurrenceLastGeneratedDate: taskRecurrenceLastGeneratedDate(task),
    recurringTaskId: taskRecurringTaskId(task),
  }
}

/** True for EITHER side of a recurring series — the template itself (`isRecurring`) or one of its generated occurrences (`recurringTaskId` set) — used purely for the small "repeat" badge on cards/rows, no permission meaning. */
export function isPartOfRecurringSeries(task) {
  return !!(task && (taskIsRecurringTemplate(task) || taskRecurringTaskId(task) != null))
}

/**
 * Find an already-materialized occurrence for `(templateId, dueDate)`.
 * Used as the primary idempotency gate so a refresh cannot POST another
 * row for the same series period when the marker update failed or the
 * backend did not echo `recurrenceLastGeneratedDate`.
 */
export function findRecurringOccurrenceForDueDate(tasks, templateId, dueDate) {
  if (templateId == null || !dueDate || !Array.isArray(tasks)) return null
  const templateKey = String(templateId)
  const dueKey = String(dueDate).slice(0, 10)
  return tasks.find(task => {
    if (!task || task.isDeleted) return false
    if (taskRecurringTaskId(task) !== templateKey) return false
    const taskDue = task.dueDate || task.due_date
    if (!taskDue) return false
    return String(taskDue).slice(0, 10) === dueKey
  }) || null
}

/**
 * True when this series already has a non-Done generated occurrence.
 * Enforces "one open occurrence per series" — do not spawn the next period
 * while the current open card is still unfinished. The template row itself
 * (`isRecurring`) is not counted here (it has no `recurringTaskId`).
 */
export function hasOpenRecurringOccurrence(tasks, templateId) {
  if (templateId == null || !Array.isArray(tasks)) return false
  const templateKey = String(templateId)
  return tasks.some(task => {
    if (!task || task.isDeleted) return false
    if (taskRecurringTaskId(task) !== templateKey) return false
    return normalizeTaskStatus(task) !== TASK_STATUSES.DONE
  })
}

/**
 * `date` advanced by `months`, clamping the day-of-month to the target
 * month's actual last day instead of overflowing into the month after (the
 * native `Date#setMonth()` quirk — e.g. Jan 31 + 1 month would otherwise
 * land on Mar 3, not Feb 28). Internal helper for the `'monthly'` case in
 * `computeNextRecurrenceDate()`.
 *
 * `targetDay` (1-31) is the EXPLICIT day-of-month to land on — set from
 * `task.recurrenceDayOfMonth` (see `TaskFormModal.vue`'s "On this day of
 * the month" field) when the caller has one; falls back to `date`'s OWN
 * day-of-month (the pre-existing, fully implicit behavior) when omitted —
 * this is what keeps any pre-existing recurring task created before this
 * field existed working exactly as it always did, with zero migration.
 */
function addMonthsClamped(date, months, targetDay = null) {
  const day = targetDay != null ? targetDay : date.getDate()
  const shifted = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const lastDayOfTargetMonth = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0).getDate()
  shifted.setDate(Math.min(day, lastDayOfTargetMonth))
  return shifted
}

/**
 * Pure function computing the DATE (not whether it's actually due yet —
 * see `isRecurrenceDue()`) of the next occurrence a recurring template
 * should generate, given its CURRENT recurrence fields. Always measured
 * from `recurrenceLastGeneratedDate` (falling back to the template's own
 * `dueDate` for a template that has never generated anything yet — the
 * template's own due date counts as "occurrence #1"). Returns `null` for
 * anything that isn't a valid recurring template.
 *
 * `'weekly'` has two mutually-exclusive modes, intentionally not combined
 * (see the inline comment below) to avoid RRULE-style complexity the spec
 * explicitly asked to avoid:
 *   - Specific weekday(s) chosen (`recurrenceDaysOfWeek` non-empty): recurs
 *     EVERY week on those day(s); `recurrenceInterval` is ignored in this
 *     mode.
 *   - No specific weekday chosen: recurs every `recurrenceInterval` weeks
 *     on the same weekday as the base date.
 *
 * `'monthly'` mirrors that same "explicit choice, falling back to the
 * implicit base-date behavior" shape via `recurrenceDayOfMonth` (1-31,
 * set by `TaskFormModal.vue`'s "On this day of the month" field) — see
 * `addMonthsClamped()`'s doc for exactly how/when the fallback applies.
 */
export function computeNextRecurrenceDate(task) {
  if (!task || !taskIsRecurringTemplate(task)) return null
  const frequency = taskRecurrenceFrequency(task)
  if (!frequency) return null
  const baseValue = taskRecurrenceLastGeneratedDate(task) || task.dueDate || task.due_date
  if (!baseValue) return null
  const base = new Date(baseValue)
  if (Number.isNaN(base.getTime())) return null
  const baseDay = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  const interval = Math.max(1, Number(taskRecurrenceInterval(task)) || 1)

  switch (frequency) {
    case 'daily': {
      const next = new Date(baseDay)
      next.setDate(next.getDate() + interval)
      return next
    }
    case 'weekly': {
      const days = taskRecurrenceDaysOfWeek(task)
        .map(Number)
        .filter(d => d >= 0 && d <= 6)
      if (days.length) {
        // Scan forward day-by-day (bounded to 2 weeks, more than enough
        // since at least one of 7 possible weekdays is selected) rather
        // than trying to jump straight to the answer — simplest correct
        // implementation for a set that can hold 1-7 arbitrary days.
        for (let offset = 1; offset <= 14; offset += 1) {
          const candidate = new Date(baseDay)
          candidate.setDate(candidate.getDate() + offset)
          if (days.includes(candidate.getDay())) return candidate
        }
        return null // unreachable given `days.length > 0`
      }
      const next = new Date(baseDay)
      next.setDate(next.getDate() + interval * 7)
      return next
    }
    case 'monthly': {
      const explicitDay = Number(taskRecurrenceDayOfMonth(task))
      const targetDay = Number.isFinite(explicitDay) && explicitDay >= 1 && explicitDay <= 31 ? explicitDay : null
      return addMonthsClamped(baseDay, interval, targetDay)
    }
    default:
      return null
  }
}

/** `'YYYY-MM-DD'` (no time component) — matches `dueDate`'s on-the-wire format everywhere else in this module. */
export function formatDateOnly(date) {
  if (!date) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * True when a recurring template's next occurrence (per
 * `computeNextRecurrenceDate()`) has arrived — due today or already
 * overdue — AND it isn't past `recurrenceEndDate` (inclusive; `null`/unset
 * means "no end date", recurs forever). Callers (`TaskBoard.vue`) generate
 * at most ONE occurrence per check even if several are overdue at once
 * (e.g. the board wasn't opened for a week) — see that method's doc for
 * why catching up one-at-a-time per check is an acceptable simplification.
 *
 * "Arrived" is deliberately the SAME day/time-aware past-deadline rule
 * already established for escalation, not a cruder calendar-day-only
 * comparison — reuses `isPastDeadline()` against the CANDIDATE next
 * occurrence's date, with the template's own `dueTime` (or the 6 PM IST
 * default when unset) standing in for that not-yet-existing occurrence's
 * `dueTime`. Concretely: a `next` date already strictly in the PAST is
 * unconditionally due; a `next` date landing on TODAY only becomes due
 * once that deadline has genuinely passed — not the instant the calendar
 * flips over to that date at midnight. (Display `daysOverdue` may be `0`
 * on that same day; recurrence still generates once the deadline passes.)
 */
export function isRecurrenceDue(task, now = new Date()) {
  const next = computeNextRecurrenceDate(task)
  if (!next) return false
  const endDate = taskRecurrenceEndDate(task)
  if (endDate) {
    const end = new Date(endDate)
    if (!Number.isNaN(end.getTime())) {
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate())
      if (next.getTime() > endDay.getTime()) return false
    }
  }
  return isPastDeadline(next, now, task.dueTime || task.due_time)
}

/**
 * True when `task`'s due calendar day is exactly `now`'s calendar day
 * (deadline time ignored). Used for recurring-occurrence reminder gating
 * (`taskEligibleForRecurringReminder`).
 */
export function isDueDateToday(task, now = new Date()) {
  if (!task) return false
  const dueDate = task.dueDate || task.due_date
  if (!dueDate) return false
  return calendarDayDiff(dueDate, now) === 0
}

/**
 * Whether an open task belongs on the "Today & Overdue" focus chip when no
 * date range is picked (`TaskBoard.vue` / sidebar counts).
 *
 * Every non-completed task whose due **calendar day** is today or earlier
 * (overdue), including recurring **occurrences**. Templates
 * (`isRecurring` with no `recurringTaskId`) and tasks with no due date
 * never match. (Reminder mail for occurrences still uses
 * `taskEligibleForRecurringReminder()` — due calendar day === today only.)
 */
export function taskMatchesTodayOverdueFocus(task, now = new Date()) {
  if (!task || task.isDeleted || isTaskComplete(task)) return false
  if (taskIsRecurringTemplate(task) && taskRecurringTaskId(task) == null) return false
  const dueDate = task.dueDate || task.due_date
  if (!dueDate) return false
  const dayDiff = calendarDayDiff(dueDate, now)
  return dayDiff != null && dayDiff >= 0
}

/**
 * Recurring occurrences get a gentle reminder only on their due calendar day
 * (not on past overdue periods — those still appear on the Today & Overdue
 * board via `taskMatchesTodayOverdueFocus`, but do not re-fire reminders).
 */
export function taskEligibleForRecurringReminder(task, now = new Date()) {
  if (!task || task.isDeleted || isTaskComplete(task)) return false
  if (taskRecurringTaskId(task) == null) return false
  return isDueDateToday(task, now)
}

/** Parse due date from a `task_reminder` inbox row (meta or dedupeKey). */
export function extractTaskReminderDueDate(notification) {
  if (!notification) return null
  const meta = notification.meta && typeof notification.meta === 'object'
    ? notification.meta
    : {}
  if (meta.dueDate) return String(meta.dueDate).slice(0, 10)
  const key = meta.dedupeKey || notification.dedupeKey || notification.dedupe_key
  if (key) {
    const parts = String(key).split(':')
    if (parts[0] === 'task_reminder' && parts.length >= 3) return String(parts[2]).slice(0, 10)
  }
  return null
}

/** True when a stored `task_reminder` row is for a past/future day — hide from inbox. */
export function isStaleRecurringTaskReminderNotification(notification, now = new Date()) {
  if (!notification || notification.type !== 'task_reminder') return false
  const dueDate = extractTaskReminderDueDate(notification)
  if (!dueDate) return false
  return calendarDayDiff(dueDate, now) !== 0
}

// ------------------------------------------------------------------------
// Field/value normalization helpers shared by the list, board and form.
// The real backend's `tasks` table is generic/unknown, so every reader is
// tolerant of multiple possible key names the server might echo back.
// ------------------------------------------------------------------------
export function taskId(task) {
  return task && (task.id ?? task._id ?? task.task_id)
}

export function taskAssigneeEmpId(task) {
  if (!task) return null
  const value = task.assignedTo ?? task.assigned_to ?? task.emp_id ?? task.empId
  return value === undefined || value === null ? null : String(value)
}

export function taskOwnerEmpId(task) {
  if (!task) return null
  const value = task.createdBy ?? task.created_by ?? task.owner_emp_id
  return value === undefined || value === null ? null : String(value)
}

function tryParseJSON(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (e) {
    return null
  }
}

export function taskAssigneeName(task) {
  if (!task) return '-'
  const parsed = tryParseJSON(task.assignedToName) || tryParseJSON(task.assigned_to_name)
  if (parsed && (parsed.title || parsed.name)) return parsed.title || parsed.name
  return task.assignedToName || task.assigned_to_name || taskAssigneeEmpId(task) || 'Unassigned'
}

export function taskOwnerName(task) {
  if (!task) return '-'
  const parsed = tryParseJSON(task.createdByName) || tryParseJSON(task.created_by_name)
  if (parsed && (parsed.title || parsed.name)) return parsed.title || parsed.name
  return task.createdByName || task.created_by_name || taskOwnerEmpId(task) || '-'
}

/**
 * "Who else is on this batch" for a task created via the "Assign to
 * multiple?" flow (`TaskFormModal.vue`'s multi-select + `TaskBoard.vue`'s
 * clone-into-N-independent-tasks design — see "Multi-Assignee Tasks" in
 * `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md`). `[]` for an ordinary,
 * non-batch task (no `groupId`).
 *
 * Two-tier lookup, in preference order:
 *   1. **Live siblings** — other tasks in `allTasks` sharing this
 *      `groupId` (excluding `task` itself). Gives a full LIVE `status`
 *      per co-assignee, but is only populated when the viewer's own
 *      already-loaded task list happens to include those siblings — true
 *      for the MANAGER who created the batch (their `allTasks` includes
 *      every task they created, `createdBy` matching every clone, per the
 *      documented `GET /pm/tasks` contract's "mine" scope), but NOT
 *      generally true for a plain assignee viewing their OWN copy (their
 *      `allTasks` is scoped to `assignedTo === them OR createdBy ===
 *      them` — a sibling clone assigned to someone ELSE matches neither).
 *   2. **Static roster fallback** — `task.coAssignees` (`{ empId, name
 *      }[]`), snapshotted once onto EVERY clone at creation time (see
 *      `TaskBoard.vue#handleSubmit`'s multi-assignee create branch), used
 *      whenever tier 1 finds no live siblings at all. This is what
 *      guarantees every assignee can always see WHO else is on the batch
 *      — the feature's core ask — even when their own view has no
 *      visibility into the siblings' live task rows; just without a live
 *      `status` for this render (`status: null`, rendered as "assigned"
 *      rather than a specific column by callers).
 */
export function coAssigneesFor(task, allTasks) {
  if (!task || !task.groupId) return []
  const ownTaskId = taskId(task)
  const liveSiblings = Array.isArray(allTasks)
    ? allTasks.filter(t => t && t.groupId === task.groupId && taskId(t) !== ownTaskId)
    : []
  if (liveSiblings.length) {
    return liveSiblings.map(sibling => ({
      empId: taskAssigneeEmpId(sibling),
      name: taskAssigneeName(sibling),
      status: normalizeTaskStatus(sibling),
    }))
  }
  const ownEmpId = taskAssigneeEmpId(task)
  return (Array.isArray(task.coAssignees) ? task.coAssignees : [])
    .filter(person => person && person.empId != null && String(person.empId) !== String(ownEmpId))
    .map(person => ({ empId: String(person.empId), name: person.name || String(person.empId), status: null }))
}

/**
 * Build the outbound payload for create/update. Sends every field the UI
 * cares about — see class doc in `task-service.js` for why (backend may
 * ignore unknown columns; that's OK, we merge locally-known fields back on
 * top of the response before showing the task in the UI).
 */
// ------------------------------------------------------------------------
// File attachments — optional supporting document on create/edit.
// Multipart field name for multer: `attachment` (see `TASK_FILE_FIELD`).
// Response may echo `attachmentName` + `attachmentUrl` and/or `file_path`
// (reimbursement-style path). Frontend also accepts `attachments[]`.
// ------------------------------------------------------------------------
export const TASK_FILE_UPLOAD_ENABLED = true
/** Multipart field name — backend should use upload.single('attachment'). */
export const TASK_FILE_FIELD = 'attachment'
/** Accept list shared by TaskFormModal's file picker (mirrors reimbursement). */
export const TASK_ATTACHMENT_ACCEPT = 'application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/pdf, text/plain, application/rtf, text/markdown, image/*, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'

/** Static files host (same hosts as `task-service.js` API_URL, without `/api/`). */
function taskFilesRoot() {
 if (process.env.NODE_ENV === "development") return 'https://ats.sattvahuman.in/'
  return 'http://192.168.0.30:5000/'
}

/**
 * Turn a relative `file_path` / absolute URL into a browser-openable href.
 * Passes through `blob:` / `data:` / `http(s):` as-is (local preview fallback).
 */
export function resolveTaskAttachmentUrl(pathOrUrl) {
  if (pathOrUrl == null || pathOrUrl === '') return null
  const value = String(pathOrUrl)
  if (/^(https?:|blob:|data:)/i.test(value)) return value
  return `${taskFilesRoot()}${value.replace(/^\//, '')}`
}

/**
 * Normalize any attachment shape the API/UI might carry into
 * `{ name, url, path } | null`. Tolerant of flat fields, a single
 * `attachment` object/string, or an `attachments[]` array (Vuexy-style).
 */
export function normalizeTaskAttachment(task) {
  if (!task || typeof task !== 'object') return null
  const fromArray = Array.isArray(task.attachments) && task.attachments.length
    ? task.attachments[0]
    : null
  const nested = (task.attachment && typeof task.attachment === 'object')
    ? task.attachment
    : null
  const pathOrUrl = task.attachmentUrl
    || task.attachment_url
    || task.file_path
    || nested?.url
    || nested?.path
    || nested?.file_path
    || fromArray?.url
    || fromArray?.path
    || fromArray?.file_path
    || (typeof task.attachment === 'string' ? task.attachment : null)
    || null
  const name = task.attachmentName
    || task.attachment_name
    || nested?.name
    || nested?.filename
    || nested?.originalname
    || fromArray?.name
    || fromArray?.filename
    || fromArray?.originalname
    || (pathOrUrl && !/^(blob:|data:)/i.test(String(pathOrUrl))
      ? String(pathOrUrl).split(/[/\\]/).pop()
      : null)
    || null
  if (!name && !pathOrUrl) return null
  return {
    name: name || 'Attachment',
    url: resolveTaskAttachmentUrl(pathOrUrl),
    path: pathOrUrl,
  }
}

export function taskHasAttachment(task) {
  return !!normalizeTaskAttachment(task)
}

/**
 * Build multipart body for create/update when a file is attached (or an
 * existing attachment is being cleared). Scalar fields are stringified;
 * arrays/objects go through JSON.stringify — backend should JSON.parse
 * those (same pattern as other multipart endpoints in this app).
 * File part uses `TASK_FILE_FIELD` (`attachment`).
 */
export function buildTaskFormData(payload, { file = null, removeAttachment = false } = {}) {
  const formData = new FormData()
  Object.keys(payload || {}).forEach(key => {
    if (key === 'attachmentFile' || key === 'removeAttachment' || key === 'mode' || key === 'id' || key === 'assignedToList') return
    const value = payload[key]
    if (value === undefined) return
    if (value === null) {
      formData.append(key, '')
      return
    }
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value))
      return
    }
    formData.append(key, String(value))
  })
  if (file) formData.append(TASK_FILE_FIELD, file)
  if (removeAttachment) formData.append('removeAttachment', 'true')
  return formData
}

/**
 * Merge server attachment echo (or local File preview) onto a task row so
 * cards/modals can show name + download link even when the API only
 * returns `file_path`, or when upload isn't live yet (blob URL preview).
 */
export function mergeTaskAttachmentFields(task, { file = null, removeAttachment = false, serverRow = null } = {}) {
  const base = { ...(task || {}) }
  if (removeAttachment && !file) {
    return {
      ...base,
      attachmentName: null,
      attachmentUrl: null,
      file_path: null,
      attachments: [],
    }
  }
  const fromServer = normalizeTaskAttachment(serverRow)
  if (fromServer) {
    return {
      ...base,
      attachmentName: fromServer.name,
      attachmentUrl: fromServer.url,
      file_path: fromServer.path,
    }
  }
  if (file) {
    const localUrl = (typeof URL !== 'undefined' && URL.createObjectURL)
      ? URL.createObjectURL(file)
      : null
    return {
      ...base,
      attachmentName: file.name || 'Attachment',
      attachmentUrl: localUrl,
      file_path: null,
    }
  }
  const existing = normalizeTaskAttachment(base)
  if (existing) {
    return {
      ...base,
      attachmentName: existing.name,
      attachmentUrl: existing.url,
      file_path: existing.path,
    }
  }
  return base
}

function normalizePayloadProjectId(value) {
  if (value === undefined || value === null) return null
  const trimmed = String(value).trim()
  return trimmed === '' ? null : trimmed
}

export function buildTaskPayload(form, userData) {
  const empId = userData?.emp_id ?? userData?.empId ?? null
  const displayName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || userData?.title || null
  const status = form.status || DEFAULT_TASK_STATUS
  const isRecurring = !!form.isRecurring
  const resolvedProjectId = normalizePayloadProjectId(form.projectId)
  // Attachment metadata only (never a File) — the binary goes via
  // FormData + `TASK_FILE_FIELD` when present; see `buildTaskFormData()`.
  // Omitted when the form never touched them (`undefined`) so a
  // status/priority-only path can't wipe an existing attachment.
  const resolvedAttachmentName = form.removeAttachment
    ? null
    : (form.attachmentName !== undefined ? (form.attachmentName || null) : undefined)
  const resolvedAttachmentUrl = form.removeAttachment
    ? null
    : (form.attachmentUrl !== undefined ? (form.attachmentUrl || null) : undefined)
  const resolvedFilePath = form.removeAttachment
    ? null
    : (form.file_path !== undefined
      ? (form.file_path || null)
      : (resolvedAttachmentUrl !== undefined ? resolvedAttachmentUrl : undefined))

  return {
    title: form.title,
    description: form.description || '',
    status,
    priority: form.priority || DEFAULT_TASK_PRIORITY,
    assignedTo: form.assignedTo != null ? String(form.assignedTo) : (empId != null ? String(empId) : null),
    assignedToName: form.assignedToName || (form.assignedTo == null || String(form.assignedTo) === String(empId) ? displayName : form.assignedToName) || null,
    dueDate: form.dueDate || null,
    // Optional deadline TIME-of-day alongside `dueDate` — see
    // `TaskFormModal.vue`'s "Due Time" field doc comment for why this is a
    // separate `H:i` (24h) text field rather than folding `dueDate` into a
    // full datetime, and `formatDueDateTime()` below for the shared
    // display formatting used by `TaskCard.vue`/`TaskListView.vue`.
    dueTime: form.dueTime || null,
    createdBy: form.createdBy != null ? String(form.createdBy) : (empId != null ? String(empId) : null),
    createdByName: form.createdByName || displayName,
    order: form.order != null ? form.order : priorityRank(form.priority),
    projectId: resolvedProjectId,
    projectName: resolvedProjectId ? (form.projectName || null) : null,
    isDeleted: false,
    ...(resolvedAttachmentName !== undefined ? { attachmentName: resolvedAttachmentName } : {}),
    ...(resolvedAttachmentUrl !== undefined ? { attachmentUrl: resolvedAttachmentUrl } : {}),
    ...(resolvedFilePath !== undefined ? { file_path: resolvedFilePath } : {}),
    // Caller passes an explicitly-derived value (see `deriveCompletedAt()`)
    // whenever it already knows the task's PREVIOUS status (edits/status
    // changes) — that's the only way to tell "just became Done" (stamp now)
    // apart from "already was Done" (keep the original timestamp) apart
    // from "moving away from Done" (clear it). Only when the caller has no
    // previous state to compare against (a brand-new task) do we derive it
    // fresh here, purely from the initial status.
    completedAt: form.completedAt !== undefined
      ? form.completedAt
      : (normalizeTaskStatus({ status }) === TASK_STATUSES.DONE ? new Date().toISOString() : null),
    // Recurrence — see the block comment above `RECURRENCE_FREQUENCIES` for
    // the full model. Every field below is force-cleared to its "off"
    // value whenever `isRecurring` is false, so toggling recurrence off in
    // the form can never leave stale frequency/interval/day/end-date data
    // sitting on a task that no longer claims to be recurring.
    isRecurring,
    recurrenceFrequency: isRecurring ? (form.recurrenceFrequency || DEFAULT_RECURRENCE_FREQUENCY) : null,
    recurrenceInterval: isRecurring ? Math.max(1, Number(form.recurrenceInterval) || 1) : null,
    recurrenceDaysOfWeek: isRecurring && form.recurrenceFrequency === 'weekly' && Array.isArray(form.recurrenceDaysOfWeek)
      ? form.recurrenceDaysOfWeek
      : [],
    // Explicit "which day of the month" for `'monthly'` — mirrors
    // `recurrenceDaysOfWeek` just above (force-cleared outside its own
    // frequency, same "no stale config once switched away" rule). `null`
    // falls back to `computeNextRecurrenceDate()`'s pre-existing IMPLICIT
    // behavior (whatever day the due/last-generated date happens to be) —
    // see `addMonthsClamped()`'s doc — so any recurring task created
    // before this field existed keeps behaving exactly as it always did.
    recurrenceDayOfMonth: isRecurring && form.recurrenceFrequency === 'monthly' && form.recurrenceDayOfMonth != null
      ? Number(form.recurrenceDayOfMonth)
      : null,
    recurrenceEndDate: isRecurring ? (form.recurrenceEndDate || null) : null,
    // NOT form-editable — a pure generation-bookkeeping field the caller
    // (`TaskBoard.vue`) passes explicitly when it needs to (preserve on
    // edit, seed on create, advance on generation); see its own doc there.
    // Left `undefined`-safe here so a caller that never mentions it at all
    // gets a clean `null` rather than accidentally inheriting something.
    recurrenceLastGeneratedDate: form.recurrenceLastGeneratedDate !== undefined ? form.recurrenceLastGeneratedDate : null,
    // Snake_case twin — some `/pm/tasks` stores only persist snake_case
    // columns; without this alias, the marker looked unset after every
    // GET and the board re-POSTed a duplicate occurrence on refresh.
    recurrence_last_generated_date: form.recurrenceLastGeneratedDate !== undefined ? form.recurrenceLastGeneratedDate : null,
    // Points a GENERATED OCCURRENCE back at its template's id — `null` for
    // the template itself and for ordinary, never-recurring tasks.
    recurringTaskId: form.recurringTaskId != null ? String(form.recurringTaskId) : null,
    recurring_task_id: form.recurringTaskId != null ? String(form.recurringTaskId) : null,
    is_recurring: isRecurring,
    // Multi-assignee-via-cloning bookkeeping (see `coAssigneesFor()`'s doc
    // above and "Multi-Assignee Tasks" in
    // `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md`) — both `null`/`[]` for an
    // ordinary, single-assignee task. `groupId` links every clone created
    // from one "Assign to multiple?" submission; `coAssignees` is a static
    // `{ empId, name }[]` snapshot of everyone ELSE in that same batch,
    // set identically by `TaskBoard.vue` on every clone at creation time.
    groupId: form.groupId != null ? String(form.groupId) : null,
    coAssignees: Array.isArray(form.coAssignees) ? form.coAssignees : [],
  }
}

/**
 * Computes the `completedAt` a status change should persist, given the
 * task's state BEFORE the change. Three cases:
 *   - Moving TO Done from something else -> stamp `now`.
 *   - Already Done, still Done (e.g. re-saving other fields) -> keep the
 *     existing timestamp untouched (never "restart the clock").
 *   - Moving AWAY from Done (reactivated) -> clear it, so a since-
 *     reactivated task is never mistaken for archived.
 * A legacy Done task with no recorded timestamp intentionally stays
 * `null` rather than being backfilled to `now` — see `isArchived()`'s doc
 * for why (a missing timestamp means "never auto-archived", the safest
 * default when we don't actually know when it was completed).
 */
export function deriveCompletedAt(previousTask, newStatus, now = new Date()) {
  const willBeDone = normalizeTaskStatus({ status: newStatus }) === TASK_STATUSES.DONE
  if (!willBeDone) return null
  const wasDone = previousTask && normalizeTaskStatus(previousTask) === TASK_STATUSES.DONE
  if (wasDone) return previousTask.completedAt || null
  return now.toISOString()
}

// ------------------------------------------------------------------------
// Auto-archive — a Done task quietly drops off the default active board
// view exactly 1 day after it was completed (see `deriveCompletedAt()`
// above for how/when `completedAt` gets stamped). It is NEVER deleted —
// `TaskBoard.vue` still holds it in `allTasks`; archiving only affects
// which computed task LIST gets rendered by default. The Date Range
// filter (`taskMatchesDateRange()` below) deliberately bypasses this, so
// a manager/employee can still pull up an archived Done task by picking
// the day it was due or completed.
// ------------------------------------------------------------------------
const ARCHIVE_AFTER_MS = 24 * 60 * 60 * 1000

export function isArchived(task, now = new Date()) {
  if (!task) return false
  if (normalizeTaskStatus(task) !== TASK_STATUSES.DONE) return false
  if (!task.completedAt) return false
  const completed = new Date(task.completedAt)
  if (Number.isNaN(completed.getTime())) return false
  return (now.getTime() - completed.getTime()) > ARCHIVE_AFTER_MS
}

/**
 * True when `task` is relevant to the given day range — its due date falls
 * in range, OR (this is what lets an already-archived Done task surface
 * again) it was completed within that range. `toDateValue` defaults to
 * `fromDateValue` so callers can pass a single day for a one-day lookup.
 * Comparisons are calendar-day-based (time-of-day is ignored on both
 * sides), matching how the picker itself only lets you choose whole days.
 */
export function taskMatchesDateRange(task, fromDateValue, toDateValue) {
  if (!task || !fromDateValue) return false
  const from = new Date(fromDateValue)
  const to = toDateValue ? new Date(toDateValue) : from
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime()
  const toDay = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime()
  const dayInRange = value => {
    if (!value) return false
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return false
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    return day >= fromDay && day <= toDay
  }
  return dayInRange(task.dueDate) || dayInRange(task.completedAt)
}

// ------------------------------------------------------------------------
// Edit history / versioning — see `TaskService#getTaskHistory`/
// `#addTaskHistoryEntries` in `task-service.js` for the dedicated-endpoint
// attempt + graceful-degradation contract, `TaskBoard.vue#recordTaskHistory`
// for where entries actually get built and sent on every mutation, and the
// "Task Edit History" section of `backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md` for
// the full backend contract writeup. Everything in this block is pure/
// synchronous — no network calls live here, only the diffing + display-
// formatting logic shared by the write side (`TaskBoard.vue`) and the read
// side (`TaskFormModal.vue`'s History panel).
// ------------------------------------------------------------------------

/**
 * The task fields worth showing in a human-readable edit history — every
 * OTHER field on a task (id, order, timestamps, `isDeleted`,
 * `recurrenceLastGeneratedDate`, `recurringTaskId`, ...) is internal
 * bookkeeping a user editing/viewing a task never consciously "changed",
 * so it's deliberately excluded per the feature spec ("don't bother
 * tracking trivial/internal bookkeeping fields").
 */
export const TASK_HISTORY_TRACKED_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'dueTime', label: 'Due Time' },
  { key: 'assignedTo', label: 'Assignee' },
  { key: 'attachmentName', label: 'Attachment' },
  { key: 'isRecurring', label: 'Recurring' },
  { key: 'recurrenceFrequency', label: 'Recurrence Frequency' },
  { key: 'recurrenceInterval', label: 'Recurrence Interval' },
  { key: 'recurrenceDaysOfWeek', label: 'Recurrence Days' },
  { key: 'recurrenceDayOfMonth', label: 'Recurrence Day of Month' },
  { key: 'recurrenceEndDate', label: 'Recurrence End Date' },
  { key: 'projectId', label: 'Project' },
]

/**
 * `dueTime` is stored as a bare 24h `"H:i"` string (e.g. `"17:00"` — see
 * `TaskFormModal.vue`'s "Due Time" field), never a full `Date`, so it's
 * parsed by hand here rather than via `moment()`/`Date` (neither reliably
 * parses a time-only string with no date component across browsers).
 * Returns `null` for anything unparseable rather than throwing.
 */
export function formatTimeOfDay(value) {
  if (!value || typeof value !== 'string') return null
  const match = value.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return null
  const hour24 = Number(match[1])
  const minute = match[2]
  if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) return null
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${hour12}:${minute} ${period}`
}

/**
 * Shared "Due: 22 Jul, 5:00 PM"-style display used everywhere a task's due
 * date is shown (`TaskCard.vue`, `TaskListView.vue`) — folds in `dueTime`
 * (see its own doc above) alongside `dueDate` when present, so every
 * display site renders the pair identically instead of each inventing its
 * own "date + time" string. Falls back to just the date when there's no
 * `dueTime` set (most tasks — it's an optional field), and to `'-'` when
 * there's no due date at all.
 */
export function formatDueDateTime(task, dateFormatter) {
  if (!task || !task.dueDate) return '-'
  const datePart = dateFormatter ? dateFormatter(task.dueDate) : task.dueDate
  const timePart = formatTimeOfDay(task.dueTime)
  return timePart ? `${datePart}, ${timePart}` : datePart
}

/** Human-readable rendering of one tracked field's value for a history entry — `rowForDisplay` is whichever "side" of the change (old task / new fields) actually carries the display-name companion field (`assignedToName`) for that value. */
function formatHistoryFieldValue(fieldKey, value, rowForDisplay) {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) return '—'
  switch (fieldKey) {
    case 'assignedTo':
      return (rowForDisplay && rowForDisplay.assignedToName) || String(value)
    case 'projectId':
      return (rowForDisplay && (rowForDisplay.projectName || rowForDisplay.project_name)) || String(value)
    case 'dueTime':
      return formatTimeOfDay(value) || String(value)
    case 'isRecurring':
      return value ? 'On' : 'Off'
    case 'recurrenceDaysOfWeek':
      return value
        .map(Number)
        .map(day => (RECURRENCE_DAYS_OF_WEEK.find(d => d.value === day) || {}).label || String(day))
        .join(', ')
    case 'description':
      // A change LOG, not a diff viewer (per spec) — a length-capped
      // preview is enough to identify/confirm a description edit
      // happened without turning the history panel into a text dump.
      return String(value).length > 60 ? `${String(value).slice(0, 60)}…` : String(value)
    default:
      return String(value)
  }
}

/** Order/type-insensitive equality for deciding whether a tracked field actually changed — e.g. `assignedTo: 1042` vs `"1042"`, or `recurrenceDaysOfWeek` arrays, should never register as a "change" purely from a type/JSON-shape difference that isn't a real edit. */
function normalizeHistoryValue(value) {
  if (Array.isArray(value)) return JSON.stringify(value)
  if (value === undefined || value === null) return null
  return String(value)
}

/**
 * Diffs `previousTask` (the task's state BEFORE this save) against
 * `updatedFields` (the new values being written — may be a full
 * `buildTaskPayload()` result for a full edit, or a small partial object
 * like `{ priority: 'High' }` for a quick action) and returns one history
 * entry per TRACKED field that both (a) is actually present as a key on
 * `updatedFields` and (b) actually changed value. Field (a) is what keeps a
 * priority-only quick change from generating a spurious "title changed"
 * entry — `updatedFields` for that call is just `{ priority }`, so `title`
 * is never even considered.
 *
 * Returns `[]` (never throws) when there's nothing to diff against (no
 * `previousTask`, e.g. a brand-new task has no history yet) or nothing
 * actually changed.
 */
export function buildHistoryEntries(previousTask, updatedFields, userData) {
  if (!previousTask || !updatedFields) return []
  const empId = userData?.emp_id ?? userData?.empId ?? null
  const changedByName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || userData?.title || 'Unknown'
  const changedAt = new Date().toISOString()

  const entries = []
  TASK_HISTORY_TRACKED_FIELDS.forEach(({ key, label }) => {
    if (!(key in updatedFields)) return
    const oldValue = previousTask[key] ?? null
    const newValue = updatedFields[key] ?? null
    if (normalizeHistoryValue(oldValue) === normalizeHistoryValue(newValue)) return
    entries.push({
      field: key,
      fieldLabel: label,
      oldValue,
      newValue,
      oldDisplay: formatHistoryFieldValue(key, oldValue, previousTask),
      newDisplay: formatHistoryFieldValue(key, newValue, updatedFields),
      changedBy: empId,
      changedByName,
      changedAt,
    })
  })
  return entries
}

/**
 * Cap on how many entries the IN-TASK-ROW fallback array (see
 * `TaskService#addTaskHistoryEntries`'s doc) is allowed to grow to before
 * the oldest entries start rolling off — a dedicated backend history
 * endpoint/table has no such limit (and is queryable/filterable, unlike a
 * column on the task row), which is exactly why that's the RECOMMENDED
 * long-term design; see the API doc's "Task Edit History" section.
 */
const MAX_STORED_HISTORY_ENTRIES = 200

/**
 * Normalize a task's `changeHistory` / `change_history` payload from the API.
 * MySQL TEXT / JSON columns and multipart form round-trips often return a
 * JSON string (or double-encoded string) instead of a real array — callers
 * that only check `Array.isArray` would silently drop ping/edit history.
 */
export function normalizeChangeHistory(value) {
  if (value == null || value === '') return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed
      if (typeof parsed === 'string') {
        try {
          const nested = JSON.parse(parsed)
          return Array.isArray(nested) ? nested : []
        } catch (e) {
          return []
        }
      }
      return []
    } catch (e) {
      return []
    }
  }
  return []
}

/** Appends `newEntries` onto `existingHistory` (tolerant of `existingHistory` being missing/not-an-array/JSON-string yet), trimming from the front once `MAX_STORED_HISTORY_ENTRIES` is exceeded. Always returns a NEW array — never mutates `existingHistory` in place, so it stays safe to use directly in a Vue-reactive assignment. */
export function appendHistoryEntries(existingHistory, newEntries) {
  const base = normalizeChangeHistory(existingHistory)
  if (!newEntries || !newEntries.length) return base
  const combined = [...base, ...newEntries]
  return combined.length > MAX_STORED_HISTORY_ENTRIES
    ? combined.slice(combined.length - MAX_STORED_HISTORY_ENTRIES)
    : combined
}

/**
 * Best-effort extraction of a server-assigned id from a `createTask()`
 * response, tolerant of multiple possible backend response shapes (raw row,
 * `{ status, data: row }`, `{ status, data: [row] }`, ...). Deliberately
 * does NOT try to merge/trust any other field from the response — see the
 * comment in `TaskBoard.vue#handleSubmit` for why (an `{status:'OK', ...}`
 * envelope's `status` key collides with our own domain `status` field, and
 * we can't reliably tell the two shapes apart, so the locally-built payload
 * — which we know is correct, since we just told the server to set exactly
 * that — remains the source of truth for everything except the id).
 */
export function extractServerId(res) {
  if (!res || typeof res !== 'object') return null
  const dataRow = Array.isArray(res.data) ? res.data[0] : res.data
  const candidates = [res.id, dataRow?.id, res.task?.id]
  const found = candidates.find(v => v !== undefined && v !== null)
  return found ?? null
}

export default {
  TASK_STATUSES,
  TASK_STATUS_COLUMNS,
  DEFAULT_TASK_STATUS,
  normalizeTaskStatus,
  isTaskComplete,
  statusVariant,
  TASK_PRIORITIES,
  TASK_PRIORITY_LEVELS,
  DEFAULT_TASK_PRIORITY,
  normalizeTaskPriority,
  priorityVariant,
  priorityRank,
  TASK_ESCALATION_LEVELS,
  getEscalationLevel,
  escalationLevelMeta,
  isTaskOverdue,
  getDaysOverdue,
  formatDaysOverdueLabel,
  TASK_DUE_TODAY_META,
  TASK_OVERDUE_META,
  isDueToday,
  RECURRENCE_FREQUENCIES,
  DEFAULT_RECURRENCE_FREQUENCY,
  RECURRENCE_DAYS_OF_WEEK,
  RECURRENCE_DAY_OF_MONTH_OPTIONS,
  isPartOfRecurringSeries,
  taskIsRecurringTemplate,
  taskRecurringTaskId,
  taskRecurrenceLastGeneratedDate,
  normalizeTaskRecurrenceFields,
  findRecurringOccurrenceForDueDate,
  hasOpenRecurringOccurrence,
  computeNextRecurrenceDate,
  formatDateOnly,
  isRecurrenceDue,
  isDueDateToday,
  taskEligibleForRecurringReminder,
  extractTaskReminderDueDate,
  isStaleRecurringTaskReminderNotification,
  taskMatchesTodayOverdueFocus,
  taskId,
  taskAssigneeEmpId,
  taskOwnerEmpId,
  taskAssigneeName,
  taskOwnerName,
  coAssigneesFor,
  buildTaskPayload,
  TASK_FILE_UPLOAD_ENABLED,
  TASK_FILE_FIELD,
  TASK_ATTACHMENT_ACCEPT,
  resolveTaskAttachmentUrl,
  normalizeTaskAttachment,
  taskHasAttachment,
  buildTaskFormData,
  mergeTaskAttachmentFields,
  formatTimeOfDay,
  formatDueDateTime,
  deriveCompletedAt,
  isArchived,
  taskMatchesDateRange,
  extractServerId,
  TASK_HISTORY_TRACKED_FIELDS,
  buildHistoryEntries,
  appendHistoryEntries,
  normalizeChangeHistory,
}
